/**
 * One toast per real-world event, not per delivery.
 *
 * Completion notifications are driven by Postgres change events. The same
 * event reaches the client more than once in normal operation:
 *   - `vehicle_appraisals` publishes with REPLICA IDENTITY DEFAULT, so an
 *     UPDATE payload carries only the primary key in `old`. A handler cannot
 *     see the previous value of `completed_at`, so EVERY later update to an
 *     already-completed appraisal (PDF reference, adopted photos, workflow
 *     bookkeeping) looks like a fresh completion.
 *   - a channel may be re-subscribed after a reconnect, a resume from
 *     background, a navigation or a re-mount, and re-deliver the same change;
 *   - more than one hook can listen to the same table.
 *
 * The guard is therefore keyed on the IDENTITY OF THE EVENT — the row id plus
 * the value that defines the transition (a completion timestamp, a status and
 * its `updated_at`) — never on array position, poll time, component instance
 * or a time window. A job that is legitimately re-completed produces a new
 * timestamp and therefore a new key, so genuine completions are never
 * suppressed.
 *
 * Keys are persisted so the guard survives re-mounts, navigation, app resume
 * and relaunch. Storage is bounded (oldest keys evicted) and entries expire, so
 * it cannot grow without limit. When storage is unavailable the in-memory set
 * still protects a running session.
 */

const STORAGE_KEY = "tm_notified_events_v1";
const MAX_KEYS = 400;
const TTL_MS = 30 * 24 * 60 * 60 * 1000;

/** Always consulted: protects a session even when persistence is unavailable. */
const seen = new Map<string, number>();
let hydrated = false;

function storage(): Storage | null {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    return null; // Safari private mode / disabled storage
  }
}

function hydrate(): void {
  if (hydrated) return;
  hydrated = true;
  const store = storage();
  if (!store) return;
  try {
    const raw = store.getItem(STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as Record<string, number>;
    const cutoff = Date.now() - TTL_MS;
    for (const [key, at] of Object.entries(parsed)) {
      if (typeof at === "number" && at > cutoff) seen.set(key, at);
    }
  } catch {
    /* corrupt payload — start clean rather than crash a notification path */
  }
}

function persist(): void {
  const store = storage();
  if (!store) return;
  try {
    // Bounded: keep the most recent MAX_KEYS entries only.
    const entries = [...seen.entries()].sort((a, b) => a[1] - b[1]);
    while (entries.length > MAX_KEYS) {
      const [oldest] = entries.shift()!;
      seen.delete(oldest);
    }
    store.setItem(STORAGE_KEY, JSON.stringify(Object.fromEntries(entries)));
  } catch {
    /* quota or disabled storage — the in-memory guard still applies */
  }
}

/**
 * True the FIRST time this event identity is seen, false for every repeat.
 * Read and write happen together and synchronously, so two listeners handling
 * the same delivery in the same tick cannot both be told to notify.
 */
export function shouldNotifyOnce(key: string): boolean {
  if (!key) return true;
  hydrate();
  if (seen.has(key)) return false;
  seen.set(key, Date.now());
  persist();
  return true;
}

/** Identity of one appraisal completion: a re-completion carries a new timestamp. */
export function appraisalCompletionKey(appraisalId: string, completedAt: string | null | undefined): string {
  return `appraisal-completed:${appraisalId}:${completedAt ?? ""}`;
}

/** Identity of one job status transition, shared by every handler that reports it. */
export function jobStatusChangeKey(
  jobId: string,
  status: string | null | undefined,
  updatedAt: string | null | undefined,
): string {
  return `job-status:${jobId}:${status ?? ""}:${updatedAt ?? ""}`;
}

/** Test-only: clear the guard between cases. */
export function __resetNotificationIdempotency(): void {
  seen.clear();
  hydrated = false;
  try { storage()?.removeItem(STORAGE_KEY); } catch { /* ignore */ }
}
